import { useCallback, useEffect, useMemo, useState } from "react";
import { Clock3, Search, FileText, BadgeCheck, Download } from "lucide-react";
import api from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function COA() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setRows((await api.get("/coa")).data); }
    catch { setError("We couldn't load the certificate directory. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const filtered = useMemo(() => rows.filter((row) => row.product.toLowerCase().includes(search.trim().toLowerCase())), [rows, search]);
  const availableCount = useMemo(() => rows.filter((r) => r.status === "available").length, [rows]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="coa-page">
      <div className="relative overflow-hidden rounded-3xl px-6 sm:px-10 py-12 sm:py-16 border border-[#efe6d2]"
        style={{ backgroundImage: "linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.5) 55%, rgba(255,255,255,0.25) 100%), url(/assets/heavenly-cloud-bg.webp)", backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="coa-banner">
        <span className="flex items-center gap-2 eyebrow"><FileText className="w-3.5 h-3.5" aria-hidden="true" /> Research documentation</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]" data-testid="coa-heading">Certificates of Analysis</h1>
        <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed" data-testid="coa-description">
          Independent third-party laboratory certificates issued by Freedom Diagnostics and ILS Laboratories. Many compounds carry multiple certificates across separate batches — stacked below — to demonstrate consistent, repeatable purity by HPLC-UV / LC-MS. Products awaiting their matching document are clearly marked as coming soon.
        </p>
      </div>
      {!loading && availableCount > 0 && (
        <div className="mt-7 flex items-start gap-3 rounded-xl border border-[#cfe6cf] bg-[#f3faf3] p-5 max-w-2xl" data-testid="coa-available-notice">
          <BadgeCheck className="w-5 h-5 shrink-0 text-[#2f7a3f] mt-0.5" aria-hidden="true" />
          <div><p className="font-semibold text-[#276434]">{availableCount} verified certificate{availableCount === 1 ? "" : "s"} available</p><p className="text-sm text-muted-foreground mt-1">Issued by an independent testing laboratory. Open or download each PDF directly below.</p></div>
        </div>
      )}
      <div className="relative max-w-sm mt-8">
        <label htmlFor="coa-search" className="sr-only">Search products in the certificate directory</label>
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
        <Input id="coa-search" data-testid="coa-search-input" placeholder="Search by product name" value={search} onChange={(event) => setSearch(event.target.value)} className="pl-11 h-11 bg-white border-border rounded-xl" />
      </div>
      {error ? <div role="alert" className="card-soft rounded-2xl p-6 mt-8" data-testid="coa-error"><p className="text-destructive">{error}</p><Button variant="outline" onClick={load} className="mt-4" data-testid="coa-retry">Try again</Button></div> :
        <div className="card-soft rounded-2xl overflow-hidden mt-8">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="coa-table">
              <caption className="sr-only">Product certificate availability</caption>
              <thead><tr className="text-left text-muted-foreground text-xs uppercase tracking-wider border-b border-border bg-[#f6f1e6]">
                <th scope="col" className="px-6 py-4 font-bold">Product</th>
                <th scope="col" className="px-6 py-4 font-bold hidden sm:table-cell">Purity</th>
                <th scope="col" className="px-6 py-4 font-bold">Certificate</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={3} className="px-6 py-16 text-center text-muted-foreground" data-testid="coa-loading">Loading certificate directory…</td></tr> : !filtered.length ?
                  <tr><td colSpan={3} className="px-6 py-16 text-center text-muted-foreground" data-testid="no-coa">No matching products found.</td></tr> :
                  filtered.map((row, index) => {
                    const certs = row.certificates && row.certificates.length ? row.certificates : (row.status === "available" ? [row] : []);
                    return (
                    <tr key={row.product_id} data-testid={`coa-row-${index}`} className="border-b border-border last:border-0 hover:bg-[#f9f6ef] transition-colors align-top">
                      <td className="px-6 py-5 font-display font-bold">
                        {row.product}
                        {certs.length > 0 && <span className="block text-xs font-normal text-muted-foreground mt-0.5" data-testid={`coa-test-count-${index}`}>{certs.length} independent {certs.length === 1 ? "test" : "tests"} on record</span>}
                      </td>
                      <td className="px-6 py-5 hidden sm:table-cell font-semibold">{row.purity || "—"}</td>
                      <td className="px-6 py-5">
                        {certs.length > 0 ? (() => {
                          const groups = [];
                          certs.forEach((c) => {
                            const key = c.strength || "";
                            let g = groups.find((x) => x.key === key);
                            if (!g) { g = { key, items: [] }; groups.push(g); }
                            g.items.push(c);
                          });
                          const multiStrength = groups.filter((g) => g.key).length > 1;
                          let pillIdx = -1;
                          return (
                            <div className="flex flex-col gap-3">
                              {groups.map((g, gi) => (
                                <div key={g.key || gi} className="flex flex-col gap-1.5">
                                  {multiStrength && g.key && (
                                    <span className="eyebrow text-[10px] text-[#8a681c]" data-testid={`coa-strength-${index}-${g.key}`}>{g.key}</span>
                                  )}
                                  {g.items.map((c) => {
                                    pillIdx += 1;
                                    return (
                                      <a key={c.coa_number || pillIdx} href={`${BACKEND}${c.certificate_url}`} target="_blank" rel="noopener noreferrer" data-testid={`coa-view-${index}-${pillIdx}`}
                                        className="inline-flex items-center gap-2 rounded-full border border-[#cfe6cf] bg-[#f3faf3] px-3 py-1.5 text-xs font-semibold text-[#276434] hover:bg-[#e8f5e8] transition-colors w-fit">
                                        <Download className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                        <span>{c.lab} · {c.purity || c.net_content || "Verified"}{c.reported ? ` · ${c.reported}` : ""}</span>
                                      </a>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                          );
                        })() : (
                          <span className="inline-flex items-center gap-2 rounded-full border border-[#dfd0a9] bg-[#faf7ee] px-3 py-1.5 text-xs font-semibold text-[#72571b]" data-testid={`coa-pending-badge-${index}`}><Clock3 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Certificate coming soon</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      }
    </div>
  );
}
